// Best-effort prefetch for admin tabs: warms the same user-scoped cache
// the admin hooks seed from, so a tab click renders instantly. Two
// triggers share one dedupe registry — an idle sweep after AdminLayout
// mounts and a hover/focus top-up on the nav links. Mounted hooks mark
// their own successful fetches here too, so a tab that's already live
// suppresses redundant prefetches.

import { writeUserCache } from "./dashboardCache";
import {
  ADMIN_QUERIES,
  adminToolUsageDef,
  instanceHealthDef,
  instanceUsageDef,
  type AdminQueryDef,
} from "./adminQueries";

export const PREFETCH_FRESH_TTL_MS = 15_000;
const IDLE_FALLBACK_MS = 1_500;
const STAGGER_MS = 300;

const lastFetchedAt = new Map<string, number>();
const inFlight = new Set<string>();

export const markAdminFetched = (resource: string): void => {
  lastFetchedAt.set(resource, Date.now());
};

export const resetAdminPrefetchState = (): void => {
  lastFetchedAt.clear();
  inFlight.clear();
};

const applyCap = (def: AdminQueryDef, value: unknown): unknown =>
  def.persistCap !== undefined && Array.isArray(value)
    ? value.slice(0, def.persistCap)
    : value;

export const prefetchAdminResource = (
  def: AdminQueryDef,
  fetchImpl: typeof fetch = fetch,
  storage?: Storage | null,
): Promise<void> => {
  if (inFlight.has(def.resource)) return Promise.resolve();
  const fetchedAt = lastFetchedAt.get(def.resource);
  if (fetchedAt !== undefined && Date.now() - fetchedAt < PREFETCH_FRESH_TTL_MS) {
    return Promise.resolve();
  }
  inFlight.add(def.resource);
  const startedAt = Date.now();
  return fetchImpl(def.url, { credentials: "include" })
    .then(async (res: Response): Promise<void> => {
      if (!res.ok) return;
      const body = (await res.json()) as unknown;
      // A mounted hook may have fetched (e.g. after a mutation) while this
      // prefetch was in flight — its data is fresher than this response, so
      // writing now would clobber the cache with pre-mutation state.
      const fetchedAt = lastFetchedAt.get(def.resource);
      if (fetchedAt !== undefined && fetchedAt >= startedAt) return;
      const value = applyCap(def, def.unwrap !== undefined ? def.unwrap(body) : body);
      if (storage === undefined) {
        writeUserCache(def.resource, value);
      } else {
        writeUserCache(def.resource, value, storage);
      }
      markAdminFetched(def.resource);
    })
    .catch((): void => {
      // best-effort — the mounted hook is the authority
    })
    .finally((): void => {
      inFlight.delete(def.resource);
    });
};

// Route segment -> default-view query. "ai-models" is absent: its data is
// the customer-scoped claws list, already cached by the dashboard hooks.
const defForTab = (tab: string): AdminQueryDef | null => {
  switch (tab) {
    case "users": return ADMIN_QUERIES.users;
    case "invites": return ADMIN_QUERIES.invites;
    case "instances": return ADMIN_QUERIES.claws;
    case "events": return ADMIN_QUERIES.events;
    case "tool-calls": return ADMIN_QUERIES.toolCalls;
    case "tool-usage": return adminToolUsageDef(new Date());
    case "resources": return ADMIN_QUERIES.resources;
    case "integrations": return ADMIN_QUERIES.providers;
    case "feature-flags": return ADMIN_QUERIES.featureFlags;
    case "secure-notes": return ADMIN_QUERIES.secureNotes;
    case "audit": return ADMIN_QUERIES.audit;
    case "status": return ADMIN_QUERIES.status;
    case "rate-limits": return ADMIN_QUERIES.rateLimits;
    case "billing": return ADMIN_QUERIES.billing;
    case "tiers": return ADMIN_QUERIES.tiers;
    // Only the catalog: the per-instance rows are keyed by tenant and are
    // network-only, so there is nothing shared to warm.
    case "plugins": return ADMIN_QUERIES.plugins;
    case "announcements": return ADMIN_QUERIES.announcements;
    case "support": return ADMIN_QUERIES.supportMessages;
    default: return null;
  }
};

export const prefetchAdminTab = (tab: string): void => {
  const def = defForTab(tab);
  if (def !== null) void prefetchAdminResource(def);
};

// Row-hover prefetch for the per-instance dashboard: warm its two default-range
// (24h) queries so the detail page paints from cache on click. Dedupe + the
// 15s fresh TTL come from prefetchAdminResource. The detail page isn't a tab,
// so it never joins the idle sweep — prefetch is hover/focus-driven only.
export const prefetchInstanceDetail = (tenantId: string): void => {
  void prefetchAdminResource(instanceHealthDef(tenantId));
  void prefetchAdminResource(instanceUsageDef(tenantId));
};

const scheduleIdle = (callback: () => void): void => {
  if (typeof requestIdleCallback === "function") {
    requestIdleCallback(() => callback(), { timeout: IDLE_FALLBACK_MS });
  } else {
    setTimeout(callback, IDLE_FALLBACK_MS);
  }
};

// Idle sweep: fire the default-view GETs for every visible tab, staggered
// so they don't compete with the active tab's own fetch (which will have
// marked itself fresh by then and be skipped).
export const prefetchAllAdminTabs = (tabs: ReadonlyArray<string>): void => {
  scheduleIdle((): void => {
    tabs.forEach((tab: string, index: number): void => {
      setTimeout((): void => prefetchAdminTab(tab), index * STAGGER_MS);
    });
  });
};
